import moment from "moment";

/** Max calendar days per backend request — keeps each call under Vercel limits. */
export const REPORT_CHUNK_DAYS = 31;

const REPORT_ARRAY_KEYS = [
  "expenses",
  "transactions",
  "guest_transactions",
  "returns",
  "guest_returns",
  "payments",
  "cash_adjustments",
  "cash_loans",
  "cash_loan_repayments",
  "all_loans",
  "all_loan_repayments",
  "cash_company_payments",
  "non_product_invoices",
  "builti_report",
];

const SUMMARY_SKIP_SUM = new Set([
  "start_date",
  "end_date",
  "opening_cash",
  "closing_cash",
  "cash_remaining_after_transfers",
  "cash_opening_source",
  "cash_prior_close_date",
  "day_closed",
  "day_close_id",
]);

/**
 * Split YYYY-MM-DD range into inclusive chunks of at most `maxDays` days.
 */
export function splitReportDateChunks(
  startDate,
  endDate,
  maxDays = REPORT_CHUNK_DAYS
) {
  const start = moment(startDate, "YYYY-MM-DD", true);
  const end = moment(endDate, "YYYY-MM-DD", true);
  if (!start.isValid() || !end.isValid()) {
    return [{ startDate, endDate }];
  }
  if (end.isBefore(start, "day")) {
    return [{ startDate: endDate, endDate: startDate }];
  }

  const chunks = [];
  let cursor = start.clone();
  while (cursor.isSameOrBefore(end, "day")) {
    const chunkEnd = moment.min(cursor.clone().add(maxDays - 1, "days"), end);
    chunks.push({
      startDate: cursor.format("YYYY-MM-DD"),
      endDate: chunkEnd.format("YYYY-MM-DD"),
    });
    cursor = chunkEnd.clone().add(1, "day");
  }
  return chunks.length ? chunks : [{ startDate, endDate }];
}

function mergeBankAccounts(parts) {
  const byId = new Map();
  for (const part of parts) {
    const rows = Array.isArray(part.bank_accounts) ? part.bank_accounts : [];
    for (const row of rows) {
      const id = String(
        row?.account?._id || row?.account?.account_no || row?.account?.name || ""
      );
      if (!id) continue;
      const prev = byId.get(id);
      if (!prev) {
        byId.set(id, {
          ...row,
          entries: [...(Array.isArray(row.entries) ? row.entries : [])],
        });
        continue;
      }
      prev.total_in = Number(prev.total_in || 0) + Number(row.total_in || 0);
      prev.total_out = Number(prev.total_out || 0) + Number(row.total_out || 0);
      prev.net_amount = prev.total_in - prev.total_out;
      prev.closing_balance = row.closing_balance;
      prev.entries = [
        ...prev.entries,
        ...(Array.isArray(row.entries) ? row.entries : []),
      ];
    }
  }
  return Array.from(byId.values());
}

function mergeSummaries(parts, startDate, endDate) {
  const first = parts[0]?.summary || {};
  const last = parts[parts.length - 1]?.summary || {};
  const out = { ...first, start_date: startDate, end_date: endDate };

  for (const part of parts.slice(1)) {
    const s = part.summary || {};
    for (const [k, v] of Object.entries(s)) {
      if (SUMMARY_SKIP_SUM.has(k)) continue;
      if (typeof v === "number" && Number.isFinite(v)) {
        out[k] = Number(out[k] || 0) + v;
      }
    }
  }

  out.opening_cash = Number(first.opening_cash || 0);
  out.closing_cash = Number(last.closing_cash || 0);
  out.cash_remaining_after_transfers = Number(
    last.cash_remaining_after_transfers ?? last.closing_cash ?? 0
  );
  out.cash_opening_source = first.cash_opening_source;
  out.cash_prior_close_date = first.cash_prior_close_date ?? null;
  const singleDay = String(startDate) === String(endDate);
  out.day_closed = singleDay ? Boolean(last.day_closed) : false;
  out.day_close_id = out.day_closed ? last.day_close_id || null : null;
  return out;
}

function mergeCashAccount(parts) {
  const cashParts = parts.map((p) => p.cash_account).filter(Boolean);
  if (!cashParts.length) return parts[0]?.cash_account ?? null;
  const first = cashParts[0];
  const last = cashParts[cashParts.length - 1];
  const total_in = cashParts.reduce((s, c) => s + Number(c.total_in || 0), 0);
  const total_out = cashParts.reduce((s, c) => s + Number(c.total_out || 0), 0);
  return {
    ...first,
    opening_balance: first.opening_balance,
    closing_balance: last.closing_balance,
    total_in,
    total_out,
    net_amount: total_in - total_out,
    entries: cashParts.flatMap((c) =>
      Array.isArray(c.entries) ? c.entries : []
    ),
    opening_source: first.opening_source,
    prior_close_date: first.prior_close_date,
    day_close: last.day_close,
  };
}

/** Merge chunk payloads into one report payload (same shape as a single API call). */
export function mergeReportPayloads(parts, query) {
  if (!parts?.length) return null;
  if (parts.length === 1) {
    return { ...parts[0], _query: query };
  }

  const merged = {
    _query: query,
    summary: mergeSummaries(parts, query.startDate, query.endDate),
    cash_account: mergeCashAccount(parts),
    bank_accounts: mergeBankAccounts(parts),
  };

  for (const key of REPORT_ARRAY_KEYS) {
    merged[key] = parts.flatMap((p) => (Array.isArray(p[key]) ? p[key] : []));
  }

  return merged;
}
